import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Clock, FileText, Package, Settings } from 'lucide-react';
import { OrdenTrabajoMateriales } from './OrdenTrabajoMateriales';
import { useOrdenesTrabajoContext, TecnicoAsignado } from '@/contexts/OrdenesTrabajoContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { OrdenTrabajoDocumentos } from './OrdenTrabajoDocumentos';
import { ObraSelectorField } from './ObraSelectorField';
import { TecnicosMultiSelector } from './TecnicosMultiSelector';

interface Cliente {
  id: string;
  nombre: string;
}

interface Tecnico {
  user_id: string;
  nombre: string;
}

import { OrdenTrabajo } from '@/contexts/OrdenesTrabajoContext';

interface OrdenTrabajoEditFormProps {
  orden: OrdenTrabajo;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const DURACIONES = [
  { value: '00:30:00', label: '30 minutos' },
  { value: '01:00:00', label: '1 hora' },
  { value: '01:30:00', label: '1.5 horas' },
  { value: '02:00:00', label: '2 horas' },
  { value: '03:00:00', label: '3 horas' },
  { value: '04:00:00', label: '4 horas' },
  { value: '08:00:00', label: 'Día completo' },
];

const TIPOS_TRABAJO = [
  { value: 'mantenimiento', label: 'Mantenimiento' },
  { value: 'revision', label: 'Revisión' },
  { value: 'instalacion', label: 'Instalación' },
  { value: 'puesta_en_marcha', label: 'Puesta en Marcha' },
  { value: 'auditoria', label: 'Auditoría' },
  { value: 'reparacion', label: 'Reparación/Avería' },
];

export const OrdenTrabajoEditForm: React.FC<OrdenTrabajoEditFormProps> = ({ 
  orden, 
  isOpen, 
  onClose, 
  onSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [tecnicos, setTecnicos] = useState<Tecnico[]>([]);
  const [tecnicosAsignados, setTecnicosAsignados] = useState<TecnicoAsignado[]>(
    orden.tecnicos_asignados || []
  );
  
  const [formData, setFormData] = useState({
    titulo: orden.titulo || '',
    descripcion: orden.descripcion || '',
    cliente_id: orden.cliente_id || '',
    tecnico_asignado_id: orden.tecnico_asignado_id || 'none',
    obra_id: orden.obra_id || 'none',
    prioridad: (orden.prioridad || 'media') as 'baja' | 'media' | 'alta' | 'urgente',
    estado: (orden.estado || 'creada') as 'creada' | 'asignada' | 'en_progreso' | 'finalizada' | 'cancelada',
    tipo_trabajo: orden.tipo_trabajo || 'reparacion',
    fecha_limite: orden.fecha_limite || '',
    hora_ejecucion: orden.hora_ejecucion || '',
    duracion_estimada: orden.duracion_estimada || '01:00:00',
    notas_admin: orden.notas_admin || '',
  });

  const { updateOrden, assignTecnicos } = useOrdenesTrabajoContext();

  const loadData = async () => {
    try {
      const { data: clientesData, error: clientesError } = await supabase
        .from('clientes')
        .select('id, nombre')
        .order('nombre');

      if (clientesError) throw clientesError;
      setClientes(clientesData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadData();
      let initial = orden.tecnicos_asignados || [];
      if (initial.length === 0 && orden.tecnico_asignado_id) {
        initial = [{
          tecnico_id: orden.tecnico_asignado_id,
          rol: 'principal',
          nombre: orden.tecnico_asignado?.nombre,
        }];
      }
      setTecnicosAsignados(initial);
      setFormData({
        titulo: orden.titulo || '',
        descripcion: orden.descripcion || '',
        cliente_id: orden.cliente_id || '',
        tecnico_asignado_id: orden.tecnico_asignado_id || 'none',
        obra_id: orden.obra_id || 'none',
        prioridad: orden.prioridad || 'media',
        estado: orden.estado || 'creada',
        tipo_trabajo: orden.tipo_trabajo || 'reparacion',
        fecha_limite: orden.fecha_limite || '',
        hora_ejecucion: orden.hora_ejecucion || '',
        duracion_estimada: orden.duracion_estimada || '01:00:00',
        notas_admin: orden.notas_admin || '',
      });
    }
  }, [isOpen, orden]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.titulo || !formData.cliente_id) return;

    // Snapshot values before unmount
    const ordenId = orden.id;
    const updates = {
      titulo: formData.titulo,
      descripcion: formData.descripcion,
      cliente_id: formData.cliente_id,
      obra_id: formData.obra_id && formData.obra_id !== 'none' ? formData.obra_id : null,
      prioridad: formData.prioridad as any,
      estado: formData.estado as any,
      tipo_trabajo: formData.tipo_trabajo,
      fecha_limite: formData.fecha_limite || null,
      hora_ejecucion: formData.hora_ejecucion || null,
      duracion_estimada: formData.duracion_estimada || null,
      notas_admin: formData.notas_admin,
    };
    const tecnicosSnapshot = tecnicosAsignados;

    // Close dialog FIRST so the form unmounts before any context mutation
    // triggers cascading re-renders (root cause of React error #185).
    onClose();

    // Mutations run after unmount; do NOT touch this component's state.
    void (async () => {
      try {
        await updateOrden(ordenId, updates as any);
        await assignTecnicos(ordenId, tecnicosSnapshot);
        onSuccess?.();
      } catch (error) {
        console.error('Error updating orden:', error);
        toast({
          title: "Error",
          description: "Error al actualizar la orden de trabajo",
          variant: "destructive",
        });
      }
    })();
  };

  const getPrioridadColor = (prioridad: string) => {
    switch (prioridad) {
      case 'baja': return 'text-blue-600';
      case 'media': return 'text-yellow-600';
      case 'alta': return 'text-orange-600';
      case 'urgente': return 'text-red-600';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Orden de Trabajo - {orden.numero_ot}</DialogTitle>
        </DialogHeader>
        
        <Tabs defaultValue="detalles" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="detalles" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Detalles
            </TabsTrigger>
            <TabsTrigger value="materiales" className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              Materiales
            </TabsTrigger>
            <TabsTrigger value="documentos" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Documentos
            </TabsTrigger>
          </TabsList>

          <TabsContent value="detalles">
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="titulo">Título *</Label>
                  <Input
                    id="titulo"
                    value={formData.titulo}
                    onChange={(e) => setFormData(prev => ({ ...prev, titulo: e.target.value }))}
                    placeholder="Título de la orden de trabajo"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="tipo_trabajo">Tipo de Trabajo</Label>
                  <Select 
                    value={formData.tipo_trabajo} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, tipo_trabajo: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPOS_TRABAJO.map((tipo) => (
                        <SelectItem key={tipo.value} value={tipo.value}>
                          {tipo.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="prioridad">Prioridad</Label>
                  <Select 
                    value={formData.prioridad} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, prioridad: value as 'baja' | 'media' | 'alta' | 'urgente' }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="baja">
                        <span className={getPrioridadColor('baja')}>Baja</span>
                      </SelectItem>
                      <SelectItem value="media">
                        <span className={getPrioridadColor('media')}>Media</span>
                      </SelectItem>
                      <SelectItem value="alta">
                        <span className={getPrioridadColor('alta')}>Alta</span>
                      </SelectItem>
                      <SelectItem value="urgente">
                        <span className={getPrioridadColor('urgente')}>Urgente</span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="descripcion">Descripción</Label>
                <Textarea
                  id="descripcion"
                  value={formData.descripcion}
                  onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                  placeholder="Descripción detallada del trabajo a realizar"
                  rows={3}
                />
              </div>

              {/* Multi Technician Selector */}
              <TecnicosMultiSelector
                value={tecnicosAsignados}
                onChange={setTecnicosAsignados}
              />

              {/* Obra Selector */}
              <ObraSelectorField
                clienteId={formData.cliente_id}
                value={formData.obra_id}
                onChange={(value) => setFormData(prev => ({ ...prev, obra_id: value }))}
              />

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="estado">Estado</Label>
                  <Select 
                    value={formData.estado} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, estado: value as 'creada' | 'asignada' | 'en_progreso' | 'finalizada' | 'cancelada' }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="creada">Creada</SelectItem>
                      <SelectItem value="asignada">Asignada</SelectItem>
                      <SelectItem value="en_progreso">En Progreso</SelectItem>
                      <SelectItem value="finalizada">Finalizada</SelectItem>
                      <SelectItem value="cancelada">Cancelada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Fecha y hora de ejecución */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fecha_limite">Fecha de Ejecución</Label>
                  <Input
                    id="fecha_limite"
                    type="date"
                    value={formData.fecha_limite}
                    onChange={(e) => setFormData(prev => ({ ...prev, fecha_limite: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hora_ejecucion">Hora</Label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="hora_ejecucion"
                      type="time"
                      className="pl-10"
                      value={formData.hora_ejecucion}
                      onChange={(e) => setFormData(prev => ({ ...prev, hora_ejecucion: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="duracion_estimada">Duración Estimada</Label>
                  <Select 
                    value={formData.duracion_estimada} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, duracion_estimada: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DURACIONES.map((dur) => (
                        <SelectItem key={dur.value} value={dur.value}>
                          {dur.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notas_admin">Notas Administrativas</Label>
                <Textarea
                  id="notas_admin"
                  value={formData.notas_admin}
                  onChange={(e) => setFormData(prev => ({ ...prev, notas_admin: e.target.value }))}
                  placeholder="Notas internas para administración"
                  rows={2}
                />
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Actualizando...' : 'Actualizar Orden'}
                </Button>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="materiales" className="mt-4">
            <OrdenTrabajoMateriales
              ordenTrabajoId={orden.id}
              empresaId={(orden as any).empresa_id}
              clienteId={orden.cliente_id}
            />
          </TabsContent>

          <TabsContent value="documentos" className="mt-4">
            <OrdenTrabajoDocumentos ordenTrabajoId={orden.id} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
